/**
 * Cloudinary Client Utility
 * Handles Cloudinary URL generation and file downloads on the client side
 */

const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'your-cloud-name';

/**
 * Extract cloud name from Cloudinary URL
 * @param {string} url - Cloudinary URL
 * @returns {string|null} Cloud name or null
 */
function extractCloudNameFromUrl(url) {
  if (!url) return null;
  const match = url.match(/res\.cloudinary\.com\/([^\/]+)/);
  return match ? match[1] : null;
}

/**
 * Build a Cloudinary transformation URL
 * @param {string} publicId - Cloudinary public ID
 * @param {object} options - Transformation options
 * @param {string} cloudNameOverride - Optional cloud name override
 * @returns {string} Transformed Cloudinary URL
 */
export function buildCloudinaryUrl(publicId, options = {}, cloudNameOverride = null) {
  const {
    format,
    width,
    height,
    crop = 'fit',
    backgroundType,
    bgColor,
    colorless,
    useBackgroundRemoval,
    quality
  } = options;

  const cloudName = cloudNameOverride || CLOUDINARY_CLOUD_NAME;

  const parts = [];

  // Size transformations
  if (width) parts.push(`w_${width}`);
  if (height) parts.push(`h_${height}`);
  if (width || height) parts.push(`c_${crop}`);

  // Background removal (must come before background color)
  if (backgroundType === 'transparent' && useBackgroundRemoval) {
    parts.push('e_background_removal');
  }

  // Background color
  if (backgroundType === 'colored' && bgColor) {
    const hex = bgColor.replace('#', '');
    parts.push(`b_rgb:${hex}`);
  }

  // Special handling for JPG format with transparent background
  if ((format === 'jpg' || format === 'jpeg') && backgroundType === 'transparent' && !bgColor) {
    // JPG cannot be transparent, force white background
    parts.push('b_white');
  }

  // Colorless/Grayscale effect
  if (colorless) {
    parts.push('e_grayscale');
  }

  // Quality
  if (quality && quality > 0 && quality <= 100) {
    parts.push(`q_${quality}`);
  }

  // Format
  if (format) {
    parts.push(`f_${format}`);
  }

  const transformation = parts.join(',');
  const baseUrl = `https://res.cloudinary.com/${cloudName}/image/upload`;
  
  if (transformation) {
    return `${baseUrl}/${transformation}/${publicId}`;
  }
  
  return `${baseUrl}/${publicId}`;
}

/**
 * Download a file from URL using blob method (works cross-origin)
 * @param {string} url - URL to download
 * @param {string} filename - Desired filename
 * @returns {Promise<void>}
 */
export async function downloadFromUrl(url, filename) {
  try {
    console.log('Starting download:', { url, filename });
    
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    console.log('Blob created:', { type: blob.type, size: blob.size });
    
    const blobUrl = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    
    console.log('Download triggered:', filename);
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);
    
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
}

/**
 * Download multiple files as a ZIP (requires server endpoint)
 * @param {Array} logoIds - Array of logo IDs
 * @param {object} transformOptions - Transformation options to apply
 * @returns {Promise<void>}
 */
export async function downloadMultipleAsZip(logoIds, transformOptions = {}) {
  try {
    const response = await fetch('/api/download/batch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        logoIds,
        transformations: transformOptions
      })
    });
    
    if (!response.ok) {
      throw new Error('Batch download failed');
    }
    
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `logos-${Date.now()}.zip`;
    link.click();
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Batch download error:', error);
    throw error;
  }
}

/**
 * Validate transformation options on client side
 * @param {object} options - Options to validate
 * @returns {object} { valid: boolean, errors: string[] }
 */
export function validateTransformOptions(options) {
  const errors = [];
  const allowedFormats = ['svg', 'png', 'jpg', 'jpeg', 'webp', 'pdf'];
  const allowedBackgroundTypes = ['transparent', 'colored', 'original'];

  if (options.format && !allowedFormats.includes(options.format.toLowerCase())) {
    errors.push(`Invalid format. Allowed: ${allowedFormats.join(', ')}`);
  }

  if (options.width && (options.width < 1 || options.width > 5000)) {
    errors.push('Width must be between 1 and 5000 pixels');
  }

  if (options.height && (options.height < 1 || options.height > 5000)) {
    errors.push('Height must be between 1 and 5000 pixels');
  }

  if (options.backgroundType && !allowedBackgroundTypes.includes(options.backgroundType)) {
    errors.push(`Invalid background type. Allowed: ${allowedBackgroundTypes.join(', ')}`);
  }

  if (options.bgColor) {
    const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!hexPattern.test(options.bgColor)) {
      errors.push('Invalid background color. Must be a valid hex color');
    }
  }

  if (options.quality && (options.quality < 1 || options.quality > 100)) {
    errors.push('Quality must be between 1 and 100');
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get optimal image format based on browser support
 * @returns {string} Optimal format
 */
export function getOptimalFormat() {
  // Check for WebP support
  const canvas = document.createElement('canvas');
  if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
    return 'webp';
  }
  return 'png';
}

/**
 * Calculate file size estimate based on dimensions and format
 * @param {number} width 
 * @param {number} height 
 * @param {string} format 
 * @returns {string} Estimated size
 */
export function estimateFileSize(width, height, format) {
  const pixels = width * height;
  let bytesPerPixel;

  switch (format) {
    case 'svg':
      return 'N/A (Vector)';
    case 'pdf':
      return '~50-200 KB';
    case 'png':
      bytesPerPixel = 4; // RGBA
      break;
    case 'jpg':
    case 'jpeg':
      bytesPerPixel = 0.5; // Compressed
      break;
    case 'webp':
      bytesPerPixel = 0.3; // More compressed
      break;
    default:
      bytesPerPixel = 1;
  }

  const bytes = pixels * bytesPerPixel;
  
  if (bytes < 1024) return `${bytes.toFixed(0)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default {
  buildCloudinaryUrl,
  downloadFromUrl,
  downloadMultipleAsZip,
  validateTransformOptions,
  getOptimalFormat,
  estimateFileSize,
  extractCloudNameFromUrl
};
